import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Upload, Search, Trash2, Tags } from "lucide-react";

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", location: "", tags: "" });
  const [openImport, setOpenImport] = useState(false);

  const load = async () => {
    const r = await api.get("/contacts", { params: { q, limit: 100 } });
    setItems(r.data.items); setTotal(r.data.total);
  };
  useEffect(() => { load(); }, [q]);

  const create = async () => {
    if (!form.name?.trim() || !form.phone?.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      await api.post("/contacts", { ...form, tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Contact added");
      setOpenNew(false); setForm({ name: "", phone: "", email: "", company: "", location: "", tags: "" }); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast.error(
        typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join("; ") :
        status ? `Save failed (HTTP ${status})` : "Network error — please retry"
      );
    }
  };

  const del = async (id) => { await api.delete(`/contacts/${id}`); toast.success("Deleted"); load(); };

  const selCount = Object.values(selected).filter(Boolean).length;
  const bulkTag = async () => {
    const tags = prompt("Enter tag(s), comma separated:");
    if (!tags) return;
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    await api.post("/contacts/bulk-tag", { ids, tags: tags.split(",").map(s => s.trim()) });
    toast.success("Tags added"); setSelected({}); load();
  };

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-7xl mx-auto" data-testid="contacts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
          <p className="text-sm text-slate-500">{total} total contacts</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={openImport} onOpenChange={setOpenImport}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-contacts-button"><Upload className="w-4 h-4 mr-1"/> Import CSV/XLSX</Button>
            </DialogTrigger>
            <ImportDialog onDone={() => { setOpenImport(false); load(); }} />
          </Dialog>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-contact-button"><Plus className="w-4 h-4 mr-1"/> New Contact</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>New contact</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                {[
                  ["name","Full name"], ["phone","WhatsApp number (E.164)"], ["email","Email"],
                  ["company","Company"], ["location","Location"], ["tags","Tags (comma-separated)"]
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label>{label}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`contact-field-${k}`}/>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-contact-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-3 border-slate-200 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search by name, phone, email…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9" data-testid="contact-search-input"/>
        </div>
        {selCount > 0 && (
          <Button variant="outline" size="sm" onClick={bulkTag} data-testid="bulk-tag-button">
            <Tags className="w-4 h-4 mr-1"/> Tag {selCount}
          </Button>
        )}
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Phone</th>
                <th className="p-3 text-left hidden md:table-cell">Email</th>
                <th className="p-3 text-left hidden md:table-cell">Location</th>
                <th className="p-3 text-left">Tags</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`contact-row-${c.id}`}>
                  <td className="p-3">
                    <Checkbox checked={!!selected[c.id]} onCheckedChange={(v)=>setSelected({...selected, [c.id]: v})} />
                  </td>
                  <td className="p-3 font-medium text-slate-900">{c.name}</td>
                  <td className="p-3 font-mono text-xs text-slate-700">{c.phone}</td>
                  <td className="p-3 text-slate-600 hidden md:table-cell">{c.email || "—"}</td>
                  <td className="p-3 text-slate-600 hidden md:table-cell">{c.location || "—"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).slice(0,3).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                  </td>
                  <td className="p-3"><Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">{c.lead_status}</Badge></td>
                  <td className="p-3">
                    <Button variant="ghost" size="icon" onClick={()=>del(c.id)} data-testid={`delete-contact-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-500"/>
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-slate-500">No contacts found</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ImportDialog({ onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/contacts/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Parse failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/contacts/import/commit", { rows: preview.rows, default_tags: [], source: "csv_import" });
      toast.success(`Imported ${r.data.inserted} contacts`); onDone();
    } catch (e) { toast.error("Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent className="bg-white max-w-3xl">
      <DialogHeader><DialogTitle>Import contacts (CSV / XLSX)</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600">
          Required columns: <code>name</code>, <code>phone</code>. Optional: <code>email</code>, <code>tags</code> (comma-separated).
        </div>
        <Input type="file" accept=".csv,.xlsx" onChange={(e)=>setFile(e.target.files?.[0])} data-testid="import-file-input"/>
        {!preview && <Button onClick={upload} disabled={!file || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="preview-import-button">Preview</Button>}
        {preview && (
          <>
            <div className="flex gap-2 text-xs">
              <Badge className="bg-emerald-100 text-emerald-800">Valid: {preview.valid}</Badge>
              <Badge className="bg-rose-100 text-rose-800">Invalid: {preview.invalid}</Badge>
              <Badge className="bg-amber-100 text-amber-800">Duplicates: {preview.duplicates}</Badge>
            </div>
            <div className="max-h-72 overflow-auto scrollbar-thin border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Phone</th><th className="p-2 text-left">Status</th></tr></thead>
                <tbody>
                  {preview.rows.slice(0, 100).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">{r.row}</td><td className="p-2">{r.name}</td><td className="p-2 font-mono">{r.normalized || r.phone}</td>
                      <td className="p-2"><Badge variant="outline" className={r.status === "valid" ? "text-emerald-700 border-emerald-200" : r.status === "duplicate" ? "text-amber-700 border-amber-200" : "text-rose-700 border-rose-200"}>{r.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button onClick={commit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="commit-import-button">Import {preview.valid} contacts</Button>
            </DialogFooter>
          </>
        )}
      </div>
    </DialogContent>
  );
}
